/**
 * Responsive toolbar overflow layout.
 * Measures available width and moves overflowing items into a 3-dot menu.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import './toolbarOverflow.css';

const OVERFLOW_BTN_FALLBACK_WIDTH = 32;
const OVERFLOW_SLOT_GAP = 2;
const TOOLBAR_GAP = 2;
const DROPDOWN_PADDING = 4;
const MEASURE_SAFETY_BUFFER = 4;

function measureElementWidth(el) {
  if (!el) {
    return 0;
  }
  const rect = el.getBoundingClientRect();
  return Math.ceil(rect.width) || el.offsetWidth || el.scrollWidth || 0;
}

function getOverflowButtonWidth(rowEl) {
  const measureBtn = rowEl?.querySelector('.toolbar-overflow-trigger--measure');
  return measureElementWidth(measureBtn) || OVERFLOW_BTN_FALLBACK_WIDTH;
}

function getToolbarRowWidth(rowEl) {
  if (!rowEl) {
    return 0;
  }
  return Math.floor(rowEl.getBoundingClientRect().width);
}

function sumItemWidths(widths, count, gap) {
  if (count <= 0) {
    return 0;
  }
  const itemsWidth = widths.slice(0, count).reduce((sum, w) => sum + w, 0);
  return itemsWidth + gap * Math.max(0, count - 1);
}

function isDividerItem(item) {
  if (!React.isValidElement(item)) {
    return false;
  }
  const dataKey = item.props?.['data-key'];
  return typeof dataKey === 'string' && dataKey.startsWith('div-');
}

function trimTrailingDividers(items, count) {
  let next = count;
  while (next > 0 && isDividerItem(items[next - 1])) {
    next -= 1;
  }
  return next;
}

function getOverflowItems(items, visibleCount) {
  let start = visibleCount;
  while (start < items.length && isDividerItem(items[start])) {
    start += 1;
  }
  return items.slice(start);
}

function getOverflowItemClassName(item) {
  const dataKey = React.isValidElement(item) ? item.props?.['data-key'] : null;
  if (dataKey === 'fontSize') {
    return 'toolbar-overflow-panel__item toolbar-overflow-panel__item--font-size';
  }
  return 'toolbar-overflow-panel__item';
}

function cloneToolbarItem(item, instanceId) {
  if (!React.isValidElement(item)) {
    return item;
  }
  return React.cloneElement(item, {key: `${instanceId}-${String(item.key)}`});
}

function isInsideOverflowInteraction(target) {
  if (!(target instanceof Node)) {
    return false;
  }
  return Boolean(
    target.closest?.('.toolbar-overflow-panel') ||
      target.closest?.('.lexical_editor_dropdown') ||
      target.closest?.('.Modal__overlay'),
  );
}

function computeVisibleCount(itemWidths, rowWidth, overflowBtnWidth, items) {
  const gap = TOOLBAR_GAP;
  const total = sumItemWidths(itemWidths, itemWidths.length, gap);

  if (rowWidth <= 0 || itemWidths.every((w) => w === 0)) {
    return itemWidths.length;
  }

  // Everything fits without an overflow button.
  if (total <= rowWidth - MEASURE_SAFETY_BUFFER) {
    return itemWidths.length;
  }

  // Reserve the overflow slot on the right, then fit as many items as possible.
  const itemsAreaWidth = Math.max(
    0,
    rowWidth - overflowBtnWidth - OVERFLOW_SLOT_GAP - MEASURE_SAFETY_BUFFER,
  );
  let count = itemWidths.length;
  while (count > 0 && sumItemWidths(itemWidths, count, gap) > itemsAreaWidth) {
    count -= 1;
  }
  return trimTrailingDividers(items, count);
}

function ToolbarOverflowMenu({ items, onOpenChange }) {
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const menuItemsRef = useRef(items);

  const setMenuOpen = useCallback(
    (nextOpen) => {
      if (nextOpen) {
        menuItemsRef.current = items.map((item) =>
          cloneToolbarItem(item, 'overflow-menu'),
        );
      }
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [items, onOpenChange],
  );

  const updatePanelPosition = useCallback(() => {
    const button = buttonRef.current;
    const panel = panelRef.current;
    if (!button || !panel) {
      return;
    }
    const { top, left } = button.getBoundingClientRect();
    panel.style.top = `${top + button.offsetHeight + DROPDOWN_PADDING}px`;
    panel.style.left = `${Math.min(
      left,
      window.innerWidth - panel.offsetWidth - 20,
    )}px`;
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    updatePanelPosition();

    const onScroll = () => updatePanelPosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      const target = event.target;
      const button = buttonRef.current;
      if (button?.contains(target)) {
        return;
      }
      if (isInsideOverflowInteraction(target)) {
        return;
      }
      setMenuOpen(false);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [open, setMenuOpen]);

  useLayoutEffect(() => {
    if (open) {
      updatePanelPosition();
    }
  }, [open, items, updatePanelPosition]);

  return (
    <>
      <button
        type="button"
        className="toolbar-item toolbar-overflow-trigger spaced"
        aria-label="More toolbar options"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setMenuOpen(!open)}
        ref={buttonRef}
      >
        <span className="icon toolbar-overflow-more" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div
            className="toolbar-overflow-panel"
            ref={panelRef}
            role="menu"
          >
            <div className="toolbar-overflow-panel__content">
              {menuItemsRef.current.map((item) => (
                <div
                  className={getOverflowItemClassName(item)}
                  key={item.key}
                  role="none"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default function ToolbarOverflowLayout({ items }) {
  const containerRef = useRef(null);
  const measureRowRef = useRef(null);
  const overflowBtnMeasureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(() => items.length);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const rafRef = useRef(null);

  const measure = useCallback(() => {
    if (isOverflowMenuOpen) {
      return;
    }
    const row = containerRef.current;
    const measureEl = measureRowRef.current;
    if (!row || !measureEl || items.length === 0) {
      return;
    }

    const rowWidth = getToolbarRowWidth(row);
    const overflowBtnWidth = getOverflowButtonWidth(row);

    // Match measure strip width to the live toolbar row for accurate sizing.
    measureEl.style.width = rowWidth > 0 ? `${rowWidth}px` : '100%';

    const measureChildren = Array.from(measureEl.children);
    if (measureChildren.length !== items.length) {
      return;
    }

    const widths = measureChildren.map((child) => measureElementWidth(child));

    const count = computeVisibleCount(widths, rowWidth, overflowBtnWidth, items);

    setVisibleCount((prev) => (prev !== count ? count : prev));
  }, [items, isOverflowMenuOpen]);

  // After paint, correct if visible items still spill (any viewport / zoom / font).
  useLayoutEffect(() => {
    if (isOverflowMenuOpen) {
      return;
    }
    const row = containerRef.current;
    const itemsEl = row?.querySelector('.toolbar__items');
    if (!itemsEl) {
      return;
    }

    const isSpilling = itemsEl.scrollWidth > itemsEl.clientWidth + 1;
    if (!isSpilling) {
      return;
    }

    setVisibleCount((current) => {
      if (current <= 0) {
        return current;
      }
      const next = trimTrailingDividers(items, current - 1);
      return next < current ? next : current - 1;
    });
  }, [visibleCount, items, isOverflowMenuOpen]);

  const scheduleMeasure = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      measure();
    });
  }, [measure]);

  useLayoutEffect(() => {
    scheduleMeasure();
    const secondPass = requestAnimationFrame(() => scheduleMeasure());
    const thirdPass = requestAnimationFrame(() => {
      requestAnimationFrame(() => scheduleMeasure());
    });
    return () => {
      cancelAnimationFrame(secondPass);
      cancelAnimationFrame(thirdPass);
    };
  }, [items, scheduleMeasure]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const ro = new ResizeObserver(scheduleMeasure);
    ro.observe(container);

    const itemsEl = container.querySelector('.toolbar__items');
    if (itemsEl) {
      ro.observe(itemsEl);
    }

    const measureEl = measureRowRef.current;
    if (measureEl) {
      ro.observe(measureEl);
    }

    const toolbar = container.closest('.toolbar');
    if (toolbar) {
      ro.observe(toolbar);
    }

    return () => ro.disconnect();
  }, [scheduleMeasure]);

  useEffect(() => {
    window.addEventListener('resize', scheduleMeasure);
    return () => window.removeEventListener('resize', scheduleMeasure);
  }, [scheduleMeasure]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const overflowItems = getOverflowItems(items, visibleCount);
  const hasOverflow = overflowItems.length > 0;

  return (
    <>
      <div className="toolbar__row" ref={containerRef}>
        <div className="toolbar__items">
          {items
            .slice(0, visibleCount)
            .map((item) => cloneToolbarItem(item, 'visible'))}
        </div>
        <div
          className={`toolbar__overflow-slot${hasOverflow ? ' toolbar__overflow-slot--active' : ''}`}
        >
          {hasOverflow && (
            <ToolbarOverflowMenu
              items={overflowItems}
              onOpenChange={setIsOverflowMenuOpen}
            />
          )}
          <button
            type="button"
            className="toolbar-item toolbar-overflow-trigger spaced toolbar-overflow-trigger--measure"
            aria-hidden="true"
            tabIndex={-1}
            ref={overflowBtnMeasureRef}
          >
            <span className="icon toolbar-overflow-more" />
          </button>
        </div>
      </div>
      <div
        className="toolbar__measure"
        ref={measureRowRef}
        aria-hidden="true"
        inert=""
      >
        {items.map((item) => (
          <div className="toolbar__measure-item" key={item.key}>
            {cloneToolbarItem(item, 'measure')}
          </div>
        ))}
      </div>
    </>
  );
}
